import React from 'react';
import { withTranslation } from 'react-i18next';
import { util } from '@helper';
import { Promise } from 'bluebird';
import _ from 'lodash';
import { CompanyAPI, JobApplicationAPI } from '@services/api';
import {
  Affix,
  Avatar,
  Badge,
  Button,
  Col,
  FlexboxGrid,
  Grid,
  IconButton,
  Modal,
  Panel,
  Row,
  Stack,
  Tooltip,
  Whisper,
  toaster,
} from 'rsuite';
import {
  FaHome,
  FaBuilding,
  FaSuitcase,
  FaFileAlt,
  FaUser,
  FaClock,
  FaMapMarkerAlt,
  FaMagic,
} from 'react-icons/fa';
import InfiniteScroll from 'react-infinite-scroller';
import FlexboxGridItem from 'rsuite/esm/FlexboxGrid/FlexboxGridItem';
import PlaceholderGraph from 'rsuite/esm/Placeholder/PlaceholderGraph';
import constants from '@helper/constants';

const { withRouter } = util;

type State = {
  loadingPreference: boolean;
  resultsByPreference: Array<object>;
  hasMorePage: boolean;

  transferJobDetails: any;

  showCVMakerBannerModal: boolean;
  showTransferJobModal: boolean;
};

class CandidateDashboardScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      resultsByPreference: [],
      loadingPreference: true,
      hasMorePage: false,

      transferJobDetails: null,

      showCVMakerBannerModal: true,
      showTransferJobModal: false,
    };
  }

  async componentDidMount(): Promise<void> {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    const t = _.first(document.getElementsByClassName('rs-content'));
    if (t) {
      _.set(t, 'style.backgroundColor', '#F6F6F6');
    }

    await Promise.allSettled([
      new Promise((resolve, reject) => {
        try {
          this.getJobRecommendation();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.isAnyNewTransferJob();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),
    ]);
  }

  private isAnyNewTransferJob = async (): Promise<void> => {
    try {
      const res = await JobApplicationAPI.isAnyNewTransferJob();
      const data = _.get(res, 'data') || [];

      let showTransferJobModal = true;
      if (data.length === 0) {
        showTransferJobModal = false;
      }

      this.setState({ transferJobDetails: data, showTransferJobModal });
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private getJobRecommendation = async (page = 1): Promise<void> => {
    try {
      const res = await CompanyAPI.getJobRecommendation(null, 9, page);
      const data: Array<any> = _.get(res, 'data') || [];
      const totalPage = Number(_.get(res, 'meta.totalPage') || 0);
      const total = Number(_.get(res, 'meta.total') || 0);

      let prevResults: Array<any> =
        _.get(this, 'state.resultsByPreference') || [];
      if (page === 1) {
        prevResults = [];
      }

      this.setState({
        resultsByPreference: prevResults.concat(data),
        loadingPreference: false,
        hasMorePage: page < totalPage && total > 0,
      });
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private redirectTo = (url: string): void => {
    const { navigate } = this.props;
    navigate(url, { replace: true });
  };

  private onSaveOrRemoveApplication = async (
    jobId: number,
    isSaved: boolean,
  ): Promise<void> => {
    try {
      let message;

      if (isSaved) {
        await JobApplicationAPI.remove(jobId);
        message = 'Job posting has been removed from your bookmark list!';

        const { resultsByPreference } = this.state;
        const idx = _.findIndex(resultsByPreference, { id: jobId });
        _.set(resultsByPreference, `[${idx}].isSaved`, false);

        this.setState({ resultsByPreference });
      } else {
        await JobApplicationAPI.save(jobId);
        message = 'Job posting has been added to your bookmark list!';

        const { resultsByPreference } = this.state;
        const idx = _.findIndex(resultsByPreference, { id: jobId });
        _.set(resultsByPreference, `[${idx}].isSaved`, true);

        this.setState({ resultsByPreference });
      }

      toaster.push(util.buildMessage(message));
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  renderSideTabs = ({ location } = this.props): JSX.Element => (
    <Col lg={5} md={5} sm={0} xs={0}>
      <Affix className="tg-affix-side-panel" top={window.innerHeight * 0.15}>
        <Stack spacing={6} direction="column">
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/home')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/home')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaHome />}
          >
            Home
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/search-companies')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/search-companies',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaBuilding />}
          >
            Company
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/search-jobs')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/search-jobs')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaSuitcase />}
          >
            Search Job
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/my-application')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/my-application',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaFileAlt />}
          >
            My Application
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/my-cv-maker')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/my-cv-maker')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaMagic />}
          >
            My CV Maker
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/profile')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/profile')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaUser />}
          >
            Profile
          </IconButton>
        </Stack>
      </Affix>
    </Col>
  );

  renderPreferenceJobs = (): React.ReactNode => {
    const {
      resultsByPreference,
      hasMorePage,
      loadingPreference: loadingCards,
    } = this.state;
    const { t } = this.props;
    const width = window.innerWidth;

    let size = 3;
    let threshold = 1 * window.innerHeight;
    if (width <= 425) {
      size = 1;
    } else if (width <= 1280) {
      size = 2;
    } else {
      threshold = 300;
      size = 3;
    }
    const chunked = _.chunk(resultsByPreference, size);

    return (
      <InfiniteScroll
        pageStart={0}
        threshold={threshold}
        loadMore={this.getJobRecommendation}
        hasMore={hasMorePage}
        loader={
          <FlexboxGrid key="load-more">
            <FlexboxGridItem
              key="load-more-item-1"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
            <FlexboxGridItem
              key="load-more-item-2"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
            <FlexboxGridItem
              key="load-more-item-3"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
          </FlexboxGrid>
        }
      >
        {loadingCards && (
          <Row className="loader-container">
            {_.range(0, 6).map((i: number) => (
              <Col key={`loader-${i}`} xs={24} sm={12} md={12} lg={8}>
                <PlaceholderGraph active />
              </Col>
            ))}
          </Row>
        )}

        {!loadingCards && resultsByPreference.length === 0 && (
          <p className="no-results-text">No results</p>
        )}

        {_.map(chunked, (data: Array<any>, index: number) => (
          <Row
            className="search-results-row"
            key={`row-searchresults-${index}`}
          >
            {_.map(data, (obj: any, idx: number) => {
              const employmentStatus = _.get(obj, 'employmentStatus.0.name');
              const company = _.get(obj, 'company');
              const companyLogo = _.get(obj, 'company.logo.url');
              const salaryHigh: number = _.get(obj, 'maxSalaryRange') || 0;
              const salaryLow: number = _.get(obj, 'minSalaryRange') || 0;
              const duration = _.get(obj, 'duration');
              const jobId = _.get(obj, 'id');
              const isSaved = _.get(obj, 'isSaved');
              const isHot = _.get(obj, 'isHot');
              const workingTypes: Array<any> = _.get(obj, 'workingTypes') || [];

              return (
                <Col
                  key={`col-searchresults-${idx}`}
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                >
                  <Panel className="search-result-card">
                    <Stack
                      direction="column"
                      spacing={20}
                      style={{ alignItems: 'flex-start' }}
                    >
                      {/* Badge */}
                      <Stack spacing={8}>
                        {isHot ? <Badge content="New" /> : <span />}
                        <Badge
                          className={
                            employmentStatus === 'Full Time'
                              ? 'rs-badge-fulltime'
                              : ''
                          }
                          content={employmentStatus}
                        />
                        {workingTypes.map((val: any) => (
                          <Badge
                            key={`workingTypes-${val.name}`}
                            content={val.name}
                          />
                        ))}
                      </Stack>

                      {/* Company Information */}
                      <Stack spacing={6}>
                        <Row>
                          <Col xs={8}>
                            <Avatar
                              circle
                              size="lg"
                              src={companyLogo}
                              alt="logo-startup"
                            />
                          </Col>
                          <Col xs={16} className="company-info">
                            <Whisper
                              placement="topStart"
                              speaker={
                                <Tooltip>
                                  <b>{_.get(company, 'name')}</b>
                                </Tooltip>
                              }
                            >
                              <p>{_.get(company, 'name')}</p>
                            </Whisper>
                            <Whisper
                              placement="topStart"
                              speaker={
                                <Tooltip>
                                  <b>{_.get(obj, 'title')}</b>
                                </Tooltip>
                              }
                            >
                              <b>{_.get(obj, 'title')}</b>
                            </Whisper>
                          </Col>
                        </Row>
                      </Stack>

                      {/* Job Details */}
                      <Stack direction="column" spacing={6}>
                        <Row>
                          <Col xs={3}>
                            <FaMapMarkerAlt />
                          </Col>
                          <Col xs={21}>
                            <p>{_.get(company, 'region')}</p>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={3}>
                            <FaClock />
                          </Col>
                          <Col xs={21}>
                            {duration > 0 && <p>{duration} month(s)</p>}
                            {duration === 0 && <p>Full Time</p>}
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={3}>
                            <FaBuilding />
                          </Col>
                          <Col xs={21}>
                            <Whisper
                              placement="topStart"
                              speaker={
                                <Tooltip>
                                  <b>{_.get(company, 'industry.name')}</b>
                                </Tooltip>
                              }
                            >
                              <p className="industry-name">
                                {_.get(company, 'industry.name')}
                              </p>
                            </Whisper>
                          </Col>
                        </Row>
                      </Stack>

                      {/* Salary range */}
                      <Stack spacing={6}>
                        <p className="salary-range">
                          {util.formatSalaryRange(salaryLow, salaryHigh)}
                        </p>
                      </Stack>

                      {/* Job Card Buttons */}
                      <Stack spacing={6}>
                        <Row>
                          <Col xs={19}>
                            <Button
                              className="btn-tg-primary"
                              block
                              appearance="primary"
                              onClick={() => {
                                // eslint-disable-next-line max-len
                                const url = `/dashboard/candidate/jobs/${jobId}`;
                                window.open(url);
                              }}
                              disabled={!obj.active}
                            >
                              {obj.active
                                ? t(
                                    constants.translation.searchJob
                                      .formApplyButtonKey,
                                  )
                                : 'Closed'}
                            </Button>
                          </Col>
                          <Col xs={5}>
                            <IconButton
                              onClick={() =>
                                this.onSaveOrRemoveApplication(jobId, isSaved)
                              }
                              appearance="default"
                              size="lg"
                              disabled={!_.get(obj, 'active')}
                              icon={
                                isSaved ? (
                                  <img
                                    // eslint-disable-next-line max-len
                                    src={`${process.env.PUBLIC_URL}/bookmark-logo-filled.png`}
                                    alt="bookmark"
                                    width={18}
                                    height={24}
                                  />
                                ) : (
                                  <img
                                    // eslint-disable-next-line max-len
                                    src={`${process.env.PUBLIC_URL}/bookmark-logo.png`}
                                    alt="bookmark"
                                    width={18}
                                    height={24}
                                  />
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </Stack>
                    </Stack>
                  </Panel>
                </Col>
              );
            })}
          </Row>
        ))}
      </InfiniteScroll>
    );
  };

  renderMainContent = (): JSX.Element | null => {
    return (
      <Col
        md={24}
        sm={24}
        xs={24}
        lg={19}
        className="user-home-candidate-main-content"
      >
        <Col xs={24}>
          <h1>Job Openings Suit You</h1>
        </Col>

        <Col xs={24}>{this.renderPreferenceJobs()}</Col>
      </Col>
    );
  };

  renderTransferJobModal = (
    { transferJobDetails } = this.state,
    { navigate } = this.props,
  ): JSX.Element => (
    <Modal
      overflow={false}
      backdrop
      open={_.get(this.state, 'showTransferJobModal')}
      onClose={() => {
        this.setState({ showTransferJobModal: false });
      }}
    >
      <Modal.Header closeButton />
      <Modal.Body>
        <img
          src={`${`${process.env.PUBLIC_URL}/bg-transfer-job.png`}`}
          className="bg-transfer-job"
          alt="bg-img"
        />
        <h3>Congratulations, We Got You A Job Transfer Recommendation!</h3>
        <p style={{ marginTop: '2em' }}>
          Given your excellent technical case study results, we are happy to
          offer you a transfer to{' '}
          <b>
            {_.get(transferJobDetails, 'jobPosting.title')} at{' '}
            {_.get(transferJobDetails, 'company.name')}
          </b>{' '}
          If you wish to accept the transfer offer, you are going to be
          interviewed with Company.
        </p>
        <p>
          Please confirm this as soon as possible as there is due date (
          {_.get(transferJobDetails, 'dueDate')}) for this confirmation by
          clicking the button below to see more information.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() =>
            navigate(
              `/dashboard/candidate/my-application/${_.get(
                transferJobDetails,
                'jobApplicationId',
              )}`,
            )
          }
          appearance="primary"
        >
          Check the Offer
        </Button>
      </Modal.Footer>
    </Modal>
  );

  renderBannerModal = (): JSX.Element => (
    <Modal
      overflow={false}
      backdrop
      open={_.get(this.state, 'showCVMakerBannerModal')}
      size="md"
      onClose={() => {
        this.setState({ showCVMakerBannerModal: false });
      }}
      onClickCapture={(e: any) => {
        const target = _.get(e, 'target');
        const classList = _.get(target, 'classList.value');

        if (classList === 'rs-modal-content') {
          window.open('/cv-maker', '_self');
        }
      }}
      dialogClassName="cvmaker-banner"
    >
      <Modal.Header closeButton />
      <Modal.Body />
    </Modal>
  );

  render(): JSX.Element | null {
    return (
      <>
        {this.renderTransferJobModal()}
        {this.renderBannerModal()}
        <Grid className="home-static-authorized-container" fluid>
          <Row>
            {this.renderSideTabs()}
            {this.renderMainContent()}
          </Row>
        </Grid>
      </>
    );
  }
}

export default withRouter(withTranslation()(CandidateDashboardScreen));
